import { useMemo } from 'react';
import {
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormGroupProps,
  FormLabel,
  Typography,
} from '@mui/material';
import { forwardRef, useState } from 'react';
import AllOptionCheckbox from './AllOptionCheckbox';
import { CheckboxGroupProps, CheckboxGroupMaxOneSelectedProps } from './types';
import { FormControlWrapper, FormLabelWrapper, HelperText, InfoTooltip } from '@shared/components';

const CheckboxGroup = forwardRef(
  (
    {
      label,
      options,
      noOptionsText,
      error,
      onChange,
      onBlur,
      name,
      value,
      color,
      disabled,
      margin,
      fullWidth,
      required,
      enableSelectAll = false,
      direction = 'column',
      labelDirection = 'column',
      tooltipText,
      labelWidth = 100,
      size = 'medium',
      customHandleChange,
      checked,
      maxOneSelected,
      backgroundColor,
    }: CheckboxGroupProps | CheckboxGroupMaxOneSelectedProps,
    ref
  ) => {
    const inputId = `${name}-id`;
    const formLabelId = `${name}-label`;
    const helperTextId = `${name}-helperText`;

    const [selectAll, setSelectAll] = useState<boolean>(value?.length === options.length);

    const toggleSelectAll = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        setSelectAll(true);
        onChange(options.map((option) => option.value));
      } else {
        setSelectAll(false);
        const disabledFilters = options
          .filter(({ disabled }) => disabled)
          .map(({ value }) => value);
        onChange(disabledFilters);
      }
      return;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (maxOneSelected) {
        const newValue = checked ? event.target.value : '';
        onChange(newValue);
        return;
      }

      if (checked) {
        const newValue = [...value, event.target.value];
        onChange(newValue);

        if (enableSelectAll && !maxOneSelected && newValue.length === options.length) {
          setSelectAll(true);
        }
      } else {
        const newValue = value.filter((item: string) => item !== event.target.value);
        onChange(newValue);

        if (enableSelectAll && !maxOneSelected && newValue.length !== options.length) {
          setSelectAll(false);
        }
      }
    };

    const directionRowSx = useMemo(
      () => getDirectionRowSx({ backgroundColor, fullWidth }),
      [backgroundColor, fullWidth]
    );
    const directionColumnSx = useMemo(
      () => getDirectionColumnSx({ backgroundColor, fullWidth }),
      [backgroundColor, fullWidth]
    );

    return (
      <FormControl
        component="fieldset"
        variant="standard"
        error={!!error}
        margin={margin}
        fullWidth={fullWidth}
        required={required}
        disabled={disabled}
      >
        <FormControlWrapper labelDirection={labelDirection}>
          {label && (
            <FormLabelWrapper direction={labelDirection} width={labelWidth} size={size}>
              {typeof label === 'string' ? (
                <FormLabel htmlFor={inputId} id={formLabelId}>
                  {label}
                </FormLabel>
              ) : (
                label
              )}
              {tooltipText && <InfoTooltip tooltipText={tooltipText} disabled={disabled} />}
            </FormLabelWrapper>
          )}

          <FormGroup id={inputId} sx={direction === 'row' ? directionRowSx : directionColumnSx}>
            {options.length > 0 ? (
              <>
                {enableSelectAll && !maxOneSelected && (
                  <AllOptionCheckbox
                    selectAll={selectAll}
                    toggleSelectAll={toggleSelectAll}
                    name={name}
                    color={color}
                    disabled={disabled}
                    sx={checkboxSx}
                  />
                )}

                {options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    label={option.label}
                    disabled={option.disabled}
                    disableTypography
                    control={
                      <Checkbox
                        size="small"
                        name={option.value}
                        checked={
                          checked ||
                          (maxOneSelected && !!value && value === option.value) ||
                          (!maxOneSelected && value.includes(option.value))
                        }
                        onChange={customHandleChange || handleChange}
                        onBlur={onBlur}
                        value={option.value}
                        id={`${option.value}-${inputId}`}
                        color={color}
                        disabled={option.disabled}
                        sx={checkboxSx}
                      />
                    }
                    componentsProps={{ typography: { variant: 'body1' } }}
                    ref={ref}
                    id={`${option.value}-${formLabelId}`}
                    style={{ marginRight: 0 }} // will be fixed in new mui release https://github.com/mui/material-ui/issues/34039
                  />
                ))}
              </>
            ) : (
              <Typography variant="body1">{noOptionsText}</Typography>
            )}
          </FormGroup>

          {error && (
            <HelperText id={helperTextId} status={'error'}>
              {error}
            </HelperText>
          )}
        </FormControlWrapper>
      </FormControl>
    );
  }
);

export default CheckboxGroup;

const checkboxSx: CheckboxProps['sx'] = {
  marginLeft: 0.25,
};

interface CustomFormGroupStyles {
  backgroundColor?: string;
  fullWidth?: boolean;
}

const getFormgroupBackgroundSx = ({
  backgroundColor,
  fullWidth = false,
}: CustomFormGroupStyles): FormGroupProps['sx'] => ({
  background: backgroundColor,
  // flexGrow: fullWidth ? 1 : undefined,
  border: (theme) => `1px solid ${theme.palette.grey.A100}`,
  paddingInline: 2,
  paddingBlock: 1,
  borderRadius: 3,
});

const getDirectionRowSx = ({
  backgroundColor,
  fullWidth = false,
}: CustomFormGroupStyles): FormGroupProps['sx'] => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, max(200px, 100%/4)), 1fr))',
  rowGap: 1,
  columnGap: 4,
  ...(backgroundColor ? getFormgroupBackgroundSx({ backgroundColor, fullWidth }) : {}),
});

const getDirectionColumnSx = ({
  backgroundColor,
  fullWidth = false,
}: CustomFormGroupStyles): FormGroupProps['sx'] => ({
  flexGrow: fullWidth ? 1 : undefined,
  gap: 0.5,
  ...(backgroundColor ? getFormgroupBackgroundSx({ backgroundColor, fullWidth }) : {}),
});
